import React from 'react';
import Chart from 'react-apexcharts/dist/react-apexcharts.esm';
import { useTranslation } from 'react-i18next';
import { isBrasilSite } from '@panel/commons/helpers';
import { selectColor } from '@panel/commons';
import { formatAmount } from '@panel/commons/helpers/formatAmount';
import { showAnonymousLeads } from '../../helpers/showAnonymousLeads';
import './tooltipDonutStyles.css';
import { Container, Wrapper } from './styles';

const DonutChart = ({ data, device }) => {
  const { t } = useTranslation();
  const isVASite = process.env.REACT_APP_BUILD_SITE === 'VAMX';
  const { userStats } = data;

  const isAnonymous = userStats && showAnonymousLeads(userStats, false);

  const getFilterType = () => {
    if (!userStats) return 'none';
    if (!isBrasilSite) {
      return userStats.viewData === 0 && userStats.whatsapp === 0 && userStats.leadForm === 0 ? 'none' : 'darken';
    }
    if (userStats.viewData === 0 && userStats.whatsapp === 0 && userStats.leadForm === 0 && userStats.anonymous === 0) {
      return 'none';
    }
    return 'darken';
  };

  const getColor = () => {
    if (!userStats) return [selectColor('grayMedium')];
    if (isAnonymous) {
      return [
        selectColor(!isVASite ? 'purple' : 'greenVaMx'),
        selectColor(!isVASite ? 'purpleLight' : 'yellowVaMx'),
        selectColor(!isVASite ? 'blueLight' : 'blueVaMx'),
        selectColor('grayMediumLight')
      ];
    }
    if (!isAnonymous) {
      return userStats.viewData === 0 && userStats.whatsapp === 0 && userStats.leadForm === 0
        ? [selectColor('grayMedium')]
        : [
            selectColor(!isVASite ? 'purple' : 'greenVaMx'),
            selectColor(!isVASite ? 'purpleLight' : 'yellowVaMx'),
            selectColor(!isVASite ? 'blueLight' : 'blueVaMx')
          ];
    }
    if (userStats.viewData === 0 && userStats.whatsapp === 0 && userStats.leadForm === 0 && userStats.anonymous === 0) {
      return [selectColor('grayMedium')];
    }
    return [
      selectColor('purple'),
      selectColor('purpleLight'),
      selectColor('blueLight'),
      selectColor('grayMediumLight')
    ];
  };

  const stateDonut = {
    series: isAnonymous
      ? [
          (userStats && userStats.leadForm) || 0.001,
          (userStats && userStats.whatsapp) || 0.001,
          (userStats && userStats.viewData) || 0.001,
          (userStats && userStats.anonymous) || 0.001
        ]
      : [
          (userStats && userStats.leadForm) || 0.001,
          (userStats && userStats.whatsapp) || 0.001,
          (userStats && userStats.viewData) || 0.001
        ],
    options: {
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        hover: {
          filter: {
            type: getFilterType(),
            value: 0.6
          }
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none',
            value: 0
          }
        }
      },
      labels: isAnonymous
        ? [
            t('panel.estadisticas.generalData.chart.formLeads'),
            t('panel.estadisticas.generalData.chart.whatsappLeads'),
            t('panel.estadisticas.generalData.chart.viewsLeads'),
            t('panel.estadisticas.generalData.chart.anonymousLeads')
          ]
        : [
            t('panel.estadisticas.generalData.chart.formLeads'),
            t('panel.estadisticas.generalData.chart.whatsappLeads'),
            t('panel.estadisticas.generalData.chart.viewsLeads')
          ],
      colors: getColor(),
      tooltip: {
        enabled: device === 'desktop',
        custom: ({ series, seriesIndex, w }) => {
          const colorSerie = w.globals.markers.colors[seriesIndex];
          const color = colorSerie.replace('#', '');
          return `<div class="arrow_boxDonut" data-panel="tooltip-donutChart-${w.globals.seriesNames[seriesIndex]}">
                <div class="text_container">
                  <span class="marker_${color}">
                    ${w.globals.seriesNames[seriesIndex]}: ${formatAmount(series[seriesIndex])}
                  </span>
                </div>
              </div>`;
        }
      },
      dataLabels: {
        enabled: false
      },
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              value: {
                show: true,
                fontSize: '20px',
                fontFamily: 'Hind',
                fontWeight: 600,
                offsetY: 0
              },
              total: {
                show: true,
                showAlways: true,
                label: '',
                offsetY: 0,
                formatter: w => {
                  const total = w.globals.seriesTotals.reduce((a, b) => a + b);
                  return formatAmount(total);
                }
              }
            }
          },
          expandOnClick: false
        }
      },
      legend: {
        position: 'bottom',
        verticalAlign: 'center',
        horizontalAlign: 'left',
        formatter: (seriesName, opts) => {
          return [seriesName, ': ', formatAmount(opts.w.globals.series[opts.seriesIndex])];
        },
        labels: {
          colors: selectColor('grayDarkLight')
        },
        markers: {
          width: 11,
          height: 11,
          radius: 12,
          offsetX: -5,
          offsetY: 1
        },
        itemMargin: {
          horizontal: 15,
          vertical: 6
        },
        onItemClick: {
          toggleDataSeries: false
        },
        onItemHover: {
          highlightDataSeries: false
        }
      }
    }
  };

  return (
    <Container>
      <Wrapper>
        <Chart type='donut' options={stateDonut.options} series={stateDonut.series} width='100%' height={'280px'} />
      </Wrapper>
    </Container>
  );
};

export default DonutChart;
